import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { MdOpenInNew } from 'react-icons/md';
import Layout from '../components/Layout';
import { User } from '../data/mockData';
import {
  getMeasurementById,
  getCreativesByMeasurementId,
  getMeasurementTagUrl,
  Measurement,
  Creative,
  TagMacroProfile,
  TagCreativeMedium,
  TagExposureType
} from '../services/measurementService';
import MeasurementHeader from '../components/MeasurementHeader';
import Select from '../components/Select';
import ExecutiveSummary from '../components/ExecutiveSummary';
import ActiveMeasurementMetricsPanel from '../components/ActiveMeasurementMetricsPanel';
import AddCreativesSection from '../components/MeasurementForm/AddCreativesSection';
import { utcTodayIso } from '../components/MeasurementForm/MeasurementDateRangeInputs';
import './MeasurementDetailsPage.css';

const MACRO_PROFILE_LABELS: Record<TagMacroProfile, string> = {
  GoogleAdManager: 'Google Ad Manager',
  DV360: 'Display & Video 360',
  CM360: 'Campaign Manager 360',
};

const TAG_SETUP_DOCS: Record<
  TagMacroProfile,
  Record<TagCreativeMedium, { href: string; label: string }>
> = {
  DV360: {
    Display: {
      href: 'https://support.google.com/displayvideo/answer/7129061?hl=en',
      label: 'How to add a third-party display tag in Display & Video 360',
    },
    CTV: {
      href: 'https://support.google.com/displayvideo/answer/7277709?hl=en',
      label: 'How to add a third-party in-stream video tag in Display & Video 360',
    },
  },
  GoogleAdManager: {
    Display: {
      href: 'https://support.google.com/admanager/answer/1746123?hl=en',
      label: 'How to add a third-party display creative in Google Ad Manager',
    },
    CTV: {
      href: 'https://support.google.com/admanager/answer/1746123?hl=en',
      label: 'How to add a third-party video creative in Google Ad Manager',
    },
  },
  CM360: {
    Display: {
      href: 'https://support.google.com/campaignmanager/answer/16765913?hl=en',
      label: 'How to add a third-party display tag in Campaign Manager 360',
    },
    CTV: {
      href: 'https://support.google.com/campaignmanager/answer/3312870?hl=en',
      label: 'How to add a third-party video tag in Campaign Manager 360',
    },
  },
};

const OpenInNewIcon = MdOpenInNew as React.ComponentType<{
  className?: string;
  'aria-hidden'?: boolean;
}>;

type DetailsTab = 'results' | 'details' | 'creatives';

const isDetailsTab = (value: string | null): value is DetailsTab =>
  value === 'results' || value === 'details' || value === 'creatives';

const tabStorageKey = (measurementId: string) => `bmp.measurementTab.${measurementId}`;

const MeasurementDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<User | null>(null);
  const [measurement, setMeasurement] = useState<Measurement | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [activeTab, setActiveTabState] = useState<DetailsTab>(() => {
    if (!id) return 'results';
    const storedTab = sessionStorage.getItem(tabStorageKey(id));
    return isDetailsTab(storedTab) ? storedTab : 'results';
  });
  const [creatives, setCreatives] = useState<Creative[]>([]);
  const [isLoadingCreatives, setIsLoadingCreatives] = useState<boolean>(false);
  const [tagCreativeMedium, setTagCreativeMedium] = useState<TagCreativeMedium | null>(null);
  const [tagMacroProfile, setTagMacroProfile] = useState<TagMacroProfile>('GoogleAdManager');
  const [tagUrls, setTagUrls] = useState<{ primary: string | null; secondary: string | null }>({
    primary: null,
    secondary: null
  });
  const [tagScriptLoading, setTagScriptLoading] = useState(false);
  const [tagScriptError, setTagScriptError] = useState<string>('');

  useEffect(() => {
    // Get user from localStorage (guaranteed to exist due to ProtectedRoute)
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    
    if (id) {
      const storedTab = sessionStorage.getItem(tabStorageKey(id));
      setActiveTabState(isDetailsTab(storedTab) ? storedTab : 'results');
      loadMeasurement(id);
      loadCreatives(id);
    }
  }, [id]);

  const setActiveTab = (tab: DetailsTab) => {
    setActiveTabState(tab);
    if (id) {
      sessionStorage.setItem(tabStorageKey(id), tab);
    }
  };

  useEffect(() => {
    setTagUrls({ primary: null, secondary: null });
    setTagScriptError('');
    setTagCreativeMedium(null);
  }, [id]);

  useEffect(() => {
    if (!id || !measurement) return;
    if (activeTab !== 'details') return;
    if (!tagCreativeMedium) {
      setTagUrls({ primary: null, secondary: null });
      setTagScriptError('');
      setTagScriptLoading(false);
      return;
    }
    const s = measurement.status.toLowerCase();
    if (s === 'reported' || s === 'failed') return;

    const exposureTypes: [TagExposureType, TagExposureType] =
      tagCreativeMedium === 'CTV'
        ? ['VastImpression', 'VastComplete']
        : ['Javascript', 'Image'];

    let cancelled = false;
    setTagScriptLoading(true);
    setTagScriptError('');
    setTagUrls({ primary: null, secondary: null });

    const opts = { macroProfile: tagMacroProfile, creativeMedium: tagCreativeMedium };
    Promise.all([
      getMeasurementTagUrl(id, { ...opts, exposureType: exposureTypes[0] }),
      getMeasurementTagUrl(id, { ...opts, exposureType: exposureTypes[1] })
    ])
      .then(([primary, secondary]) => {
        if (!cancelled) setTagUrls({ primary, secondary });
      })
      .catch((err) => {
        if (!cancelled) {
          setTagScriptError(err instanceof Error ? err.message : 'Failed to load tracking URLs');
          setTagUrls({ primary: null, secondary: null });
        }
      })
      .finally(() => {
        if (!cancelled) setTagScriptLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id, measurement, activeTab, tagMacroProfile, tagCreativeMedium]);

  const loadMeasurement = async (measurementId: string) => {
    try {
      setIsLoading(true);
      const data = await getMeasurementById(measurementId);
      if (data) {
        setMeasurement(data);
      } else {
        setError('Measurement not found');
      }
    } catch (err) {
      setError('Failed to load measurement');
    } finally {
      setIsLoading(false);
    }
  };

  const loadCreatives = async (measurementId: string) => {
    try {
      setIsLoadingCreatives(true);
      const data = await getCreativesByMeasurementId(measurementId);
      setCreatives(data || []);
    } catch (err) {
      // Creatives are non-critical for page load; log to console only
      // and keep the rest of the page functional.
      // eslint-disable-next-line no-console
      console.error('Failed to load creatives', err);
    } finally {
      setIsLoadingCreatives(false);
    }
  };

  const canAddCreatives =
    measurement != null &&
    measurement.type.toLowerCase() === 'tag' &&
    (measurement.status.toLowerCase() === 'upcoming' ||
      measurement.status.toLowerCase() === 'active') &&
    measurement.endDate.slice(0, 10) >= utcTodayIso();

  
  // Format date helper
  const formatDate = (dateString: string): string => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };
  
  // Format enum value for display (converts camelCase/PascalCase to Title Case)
  const formatEnumValue = (value: string): string => {
    if (!value) return 'Not specified';
    
    // Handle common acronyms first (B2B, B2C, etc.)
    const acronyms: { [key: string]: string } = {
      'b2b': 'B2B',
      'b2c': 'B2C',
      'B2B': 'B2B',
      'B2C': 'B2C',
      'b2B': 'B2B',
      'b2C': 'B2C'
    };
    
    const lowerValue = value.toLowerCase();
    if (acronyms[lowerValue]) {
      return acronyms[lowerValue];
    }
    
    // Convert camelCase/PascalCase to Title Case, but preserve acronyms
    return value
      .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space between lowercase and uppercase
      .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2') // Add space between uppercase letters followed by lowercase
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  const tagSetupDocs =
    tagCreativeMedium && tagMacroProfile
      ? TAG_SETUP_DOCS[tagMacroProfile][tagCreativeMedium]
      : null;

  if (!user) {
    return <div className="loading">Loading...</div>;
  }

  if (isLoading) {
    return (
      <Layout user={user}>
        <div className="measurement-details-page">
          <div className="loading-state">Loading measurement details...</div>
        </div>
      </Layout>
    );
  }

  if (error || !measurement) {
    return (
      <Layout user={user}>
        <div className="measurement-details-page">
          <div className="error-state">
            <h2>Error</h2>
            <p>{error || 'Measurement not found'}</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="measurement-details-page">
        <MeasurementHeader measurement={measurement} />

        <div className="tabs-container">
          <div className="tabs-header">
            <button
              className={`tab-button ${activeTab === 'results' ? 'active' : ''}`}
              onClick={() => setActiveTab('results')}
            >
              Results
            </button>
            <button
              className={`tab-button ${activeTab === 'details' ? 'active' : ''}`}
              onClick={() => setActiveTab('details')}
            >
              Details
            </button>
            <button
              className={`tab-button ${activeTab === 'creatives' ? 'active' : ''}`}
              onClick={() => setActiveTab('creatives')}
            >
              Creatives
            </button>
          </div>

          <div className="tab-content">
            {activeTab === 'results' && (
              <div className="results-tab">
                {measurement.type.toLowerCase() === 'tag' &&
                  measurement.status.toLowerCase() === 'active' && (
                    <ActiveMeasurementMetricsPanel measurementId={measurement.id} />
                  )}
                {measurement.status.toLowerCase() === 'failed' && (
                  <div className="measurement-failed-banner" role="alert">
                    <p>
                      Campaign results could not be generated (missing impression data or prediction error). This
                      measurement is marked as failed.
                    </p>
                  </div>
                )}
                {(measurement.status.toLowerCase() === 'reported') && measurement.brandLifts && (
                  <ExecutiveSummary
                    brandLifts={measurement.brandLifts}
                    benchmarks={measurement.benchmarks}
                    measurementId={measurement.id}
                    measurementName={measurement.name}
                    defaultExecutiveSummaryText={measurement.executiveSummaryText}
                  />
                )}
              </div>
            )}

            {activeTab === 'creatives' && (
              <div className="creatives-tab">
                <div className="details-section">
                  <h2>Creatives {creatives.length > 0 ? `(${creatives.length})` : ''}</h2>
                  {canAddCreatives && id && (
                    <AddCreativesSection
                      measurementId={id}
                      onUploaded={() => loadCreatives(id)}
                    />
                  )}
                  {isLoadingCreatives && (
                    <div className="loading-state">Loading creatives...</div>
                  )}
                  {!isLoadingCreatives && creatives.length === 0 && (
                    <p>No creatives have been uploaded for this measurement.</p>
                  )}
                  {!isLoadingCreatives && creatives.length > 0 && (
                    <div className="creatives-grid">
                      {creatives.map((creative) => (
                        <div
                          key={creative.id}
                          className="creative-card"
                        >
                          <div className="creative-preview">
                            {creative.type === 'image' ? (
                              <img
                                src={creative.url}
                                alt={creative.name}
                                className="preview-image"
                              />
                            ) : creative.type === 'video' ? (
                              <video
                                src={creative.url}
                                className="preview-video"
                                controls
                              />
                            ) : (
                              <div className="file-icon" />
                            )}
                          </div>
                          <div className="creative-info">
                            <h4 className="creative-name">{creative.name}</h4>
                            <span className="creative-type">{creative.type.toUpperCase()}</span>
                            {creative.externalId && (
                              <div className="creative-ad-manager-id">
                                <label>External ID:</label>
                                <span>{creative.externalId}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'details' && (
              <div className="details-tab">
                <div className="details-section">
                  <h2>Basic Information</h2>
                  <div className="info-grid">
                    <div className="info-item">
                      <label>Name</label>
                      <span>{measurement.name}</span>
                    </div>
                    <div className="info-item">
                      <label>Advertiser</label>
                      <span>{measurement.advertiser || 'Not specified'}</span>
                    </div>
                    <div className="info-item">
                      <label>Brand</label>
                      <span>{measurement.brand || 'Not specified'}</span>
                    </div>
                    <div className="info-item">
                      <label>Start Date</label>
                      <span>{formatDate(measurement.startDate)}</span>
                    </div>
                    <div className="info-item">
                      <label>End Date</label>
                      <span>{formatDate(measurement.endDate)}</span>
                    </div>
                    <div className="info-item">
                      <label>Created</label>
                      <span>{new Date(measurement.createdAt).toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {measurement.brandClassification && (
                  <div className="details-section">
                    <h2>Brand Classification</h2>
                    <div className="info-grid">
                      <div className="info-item">
                        <label>Brand Position</label>
                        <span>{formatEnumValue(measurement.brandClassification.brandPosition)}</span>
                      </div>
                      <div className="info-item">
                        <label>Brand Scope</label>
                        <span>{formatEnumValue(measurement.brandClassification.brandScope)}</span>
                      </div>
                      <div className="info-item">
                        <label>Business Type</label>
                        <span>{formatEnumValue(measurement.brandClassification.businessType)}</span>
                      </div>
                      <div className="info-item">
                        <label>Purchase Cycle</label>
                        <span>{formatEnumValue(measurement.brandClassification.purchaseCycle)}</span>
                      </div>
                      <div className="info-item">
                        <label>Industry Sector (Classification)</label>
                        <span>{formatEnumValue(measurement.brandClassification.industrySector)}</span>
                      </div>
                      <div className="info-item">
                        <label>Industry Subcategory</label>
                        <span>{formatEnumValue(measurement.brandClassification.industrySubcategory)}</span>
                      </div>
                    </div>
                  </div>
                )}

                {measurement.status.toLowerCase() !== 'reported' && (
                  <div className="details-section">
                    <h2>Tracking URLs</h2>
                    <p className="script-description">
                      {!tagCreativeMedium &&
                        'Select an environment to generate tracking URLs for your ad server. Remember to handle macros.'}
                      {tagCreativeMedium === 'Display' &&
                        'Use the Javascript URL in a script loader or the image URL as a pixel, per your ad server. Remember to handle macros.'}
                      {tagCreativeMedium === 'CTV' &&
                        'Place the VAST Impression and VAST Complete URLs on the corresponding video events in your ad server. Remember to handle macros.'}
                    </p>
                    <div className="script-tag-options">
                      <Select<TagCreativeMedium>
                        id="tag-environment"
                        label="Environment"
                        value={tagCreativeMedium}
                        placeholder="Select…"
                        options={[
                          { value: 'Display', label: 'Display' },
                          { value: 'CTV', label: 'CTV' },
                        ]}
                        onChange={setTagCreativeMedium}
                        disabled={tagScriptLoading}
                      />
                      <Select<TagMacroProfile>
                        id="tag-macro-profile"
                        label="Macro profile"
                        value={tagMacroProfile}
                        options={[
                          { value: 'GoogleAdManager', label: MACRO_PROFILE_LABELS.GoogleAdManager },
                          { value: 'DV360', label: MACRO_PROFILE_LABELS.DV360 },
                          { value: 'CM360', label: MACRO_PROFILE_LABELS.CM360 },
                        ]}
                        onChange={setTagMacroProfile}
                        disabled={tagScriptLoading}
                      />
                    </div>
                    {tagSetupDocs && (
                      <a
                        className="tag-docs-link"
                        href={tagSetupDocs.href}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span>{tagSetupDocs.label}</span>
                        <OpenInNewIcon className="tag-docs-link-icon" aria-hidden />
                        <span className="sr-only"> (opens in a new tab)</span>
                      </a>
                    )}
                    {tagCreativeMedium && tagScriptLoading && (
                      <div className="script-loading">Loading URLs…</div>
                    )}
                    {tagScriptError && (
                      <div className="script-inline-error">{tagScriptError}</div>
                    )}
                    {!tagScriptLoading && tagUrls.primary && tagUrls.secondary && (
                      <div className="tag-snippets-stack">
                        <div className="script-container">
                          <div className="script-header">
                            <span>
                              {tagCreativeMedium === 'CTV' ? 'VAST Impression' : 'Javascript URL'}
                            </span>
                            <button
                              type="button"
                              className="copy-button"
                              onClick={() => {
                                navigator.clipboard.writeText(tagUrls.primary!);
                              }}
                            >
                              Copy to Clipboard
                            </button>
                          </div>
                          <pre className="script-code">
                            <code>{tagUrls.primary}</code>
                          </pre>
                        </div>
                        <div className="script-container">
                          <div className="script-header">
                            <span>
                              {tagCreativeMedium === 'CTV' ? 'VAST Complete' : 'Image URL'}
                            </span>
                            <button
                              type="button"
                              className="copy-button"
                              onClick={() => {
                                navigator.clipboard.writeText(tagUrls.secondary!);
                              }}
                            >
                              Copy to Clipboard
                            </button>
                          </div>
                          <pre className="script-code">
                            <code>{tagUrls.secondary}</code>
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default MeasurementDetailsPage;
